/*
  Kiwi Ears · how we tune page (Rene 2026-08-31) — sections/tp-*.

  IT TOGGLES MARKUP, IT NEVER BUILDS A PANEL. Every graph view, pillar,
  frequency region, transducer role, process stage, transition state, genre
  and personality is a customizer block server-rendered by its section;
  this file only decides which panel shows (the one exception is the
  personality curve, whose SVG path `d` is swapped from the selected
  block's data attribute — an attribute swap, not markup building).
  Loaded by several sections, so the run-once guard is load-bearing.
*/
(function () {
  'use strict';
  if (window.__kiwiTune) return;
  window.__kiwiTune = true;

  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return [].slice.call((r || document).querySelectorAll(s)); };

  /* Generic workbench: buttons carry btnAttr, panels carry panelAttr with
     matching keys. stateAttr (optional) mirrors the active key (or the
     button's own data-* named by stateFrom) onto the container for CSS.
     pressed=true uses aria-pressed (toggle group); otherwise tablist
     semantics with roving tabindex and arrow keys. */
  function wire(root, btnAttr, panelAttr, opts) {
    if (!root) return;
    opts = opts || {};
    var btns = $$('[' + btnAttr + ']', root);
    if (!btns.length) return;

    var select = function (btn) {
      var key = btn.getAttribute(btnAttr);
      btns.forEach(function (b) {
        var on = b === btn;
        if (opts.pressed) {
          b.setAttribute('aria-pressed', String(on));
        } else {
          b.setAttribute('aria-selected', String(on));
          b.setAttribute('tabindex', on ? '0' : '-1');
        }
        b.classList.toggle('is-on', on);
      });
      $$('[' + panelAttr + ']', root).forEach(function (p) {
        p.hidden = p.getAttribute(panelAttr) !== key;
      });
      if (opts.stateAttr) {
        root.setAttribute(opts.stateAttr, opts.stateFrom ? (btn.getAttribute(opts.stateFrom) || key) : key);
      }
      if (opts.onSelect) opts.onSelect(btn);
    };

    root.addEventListener('click', function (e) {
      var b = e.target.closest('[' + btnAttr + ']');
      if (b && btns.indexOf(b) !== -1) select(b);
    });
    if (!opts.pressed) {
      root.addEventListener('keydown', function (e) {
        if (['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'].indexOf(e.key) === -1) return;
        var cur = btns.indexOf(document.activeElement);
        if (cur === -1) return;
        e.preventDefault();
        var next = cur;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (cur + 1) % btns.length;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (cur + btns.length - 1) % btns.length;
        if (e.key === 'Home') next = 0;
        if (e.key === 'End') next = btns.length - 1;
        btns[next].focus();
        select(btns[next]);
      });
    }
    return { btns: btns, select: select };
  }

  wire($('[data-tp-hero]'), 'data-tp-view-btn', 'data-tp-view-cap', { pressed: true, stateAttr: 'data-tp-view' });
  wire($('[data-tp-pillars]'), 'data-tp-pillar', 'data-tp-pillar-panel', {});
  wire($('[data-tp-freq]'), 'data-tp-band-btn', 'data-tp-band-panel', { stateAttr: 'data-tp-band' });
  wire($('[data-tp-integrate]'), 'data-tp-driver', 'data-tp-driver-panel', { stateAttr: 'data-tp-node', stateFrom: 'data-node' });
  var procRoot = $('[data-tp-process]');
  var proc = wire(procRoot, 'data-tp-stage', 'data-tp-stage-panel', {});

  /* Process rail autoplay: the active stage NUMBER is a timer — its bar
     (.tp-stagetimer, painted here via scaleX) fills over the configured hold,
     then the next stage opens, looping. It runs only while the bench is on
     screen and the tab visible, freezes under the pointer, skips reduced
     motion, and any REAL interaction (pointer or keyboard on the rail) stops
     it for good — a person reading stage 03 must not have it yanked away.
     Advancing goes through the same select() the clicks use, so tab state,
     panels and a11y attributes cannot drift. */
  (function (root, api) {
    if (!root || !api || api.btns.length < 2) return;
    if (root.getAttribute('data-tp-auto') === 'false') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var HOLD = (parseFloat(root.getAttribute('data-tp-auto-secs')) || 8) * 1000;
    var btns = api.btns;
    var idx = 0, elapsed = 0, last = null, raf = 0;
    var inView = false, hover = false, stopped = false;

    function paint(p) {
      var el = btns[idx].querySelector('.tp-stagetimer');
      if (el) el.style.transform = 'scaleX(' + p + ')';
    }
    function running() { return !stopped && inView && !hover && !document.hidden; }
    function tick(ts) {
      raf = 0;
      if (!running()) { last = null; return; }
      if (last !== null) elapsed += ts - last;
      last = ts;
      if (elapsed >= HOLD) {
        paint(0);
        elapsed = 0;
        idx = (idx + 1) % btns.length;
        api.select(btns[idx]);
      } else {
        paint(elapsed / HOLD);
      }
      raf = requestAnimationFrame(tick);
    }
    function kick() { if (!raf && running()) { last = null; raf = requestAnimationFrame(tick); } }
    function stop() {
      if (stopped) return;
      stopped = true;
      btns.forEach(function (b) {
        var t = b.querySelector('.tp-stagetimer');
        if (t) t.style.transform = 'scaleX(0)';
      });
      root.setAttribute('data-tp-autoplay', 'off');
    }

    /* capture phase, so stop() lands before wire()'s own click/key handlers */
    root.addEventListener('pointerdown', function (e) {
      if (e.target.closest('[data-tp-stage]')) stop();
    }, true);
    root.addEventListener('keydown', stop, true);

    root.addEventListener('pointerenter', function () { hover = true; });
    root.addEventListener('pointerleave', function () { hover = false; kick(); });
    document.addEventListener('visibilitychange', kick);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        inView = es[0].isIntersecting;
        kick();
      }, { threshold: 0.25 }).observe(root);
    } else {
      inView = true;
      kick();
    }
  })(procRoot, proc);
  wire($('[data-tp-transition]'), 'data-tp-mode-btn', 'data-tp-mode-cap', { pressed: true, stateAttr: 'data-tp-mode' });
  wire($('[data-tp-genres]'), 'data-tp-genre', 'data-tp-genre-panel', {});

  /* personalities: also morph the shared curve to the block's path */
  var personas = $('[data-tp-personas]');
  if (personas) {
    var line = $('[data-tp-persona-line]', personas);
    var glow = $('[data-tp-persona-glow]', personas);
    var area = $('[data-tp-persona-area]', personas);
    wire(personas, 'data-tp-persona', 'data-tp-persona-panel', {
      onSelect: function (btn) {
        var d = btn.getAttribute('data-line');
        if (!d || !line) return;
        line.setAttribute('d', d);
        if (glow) glow.setAttribute('d', d);
        if (area) area.setAttribute('d', d + 'L716 280H44Z');
        line.classList.remove('tp-redraw');
        void line.getBoundingClientRect();
        line.classList.add('tp-redraw');
      }
    });
  }

  /* ------------------------------------------------------------ reveals */
  function reveals() {
    if (!window.gsap || !window.ScrollTrigger) return;
    window.gsap.registerPlugin(window.ScrollTrigger);
    window.gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', function () {
      $$('[data-tp-rev]').forEach(function (el) {
        window.gsap.fromTo(el, { autoAlpha: 0, y: 22 }, {
          autoAlpha: 1, y: 0, duration: 0.7, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 92%', once: true }
        });
      });
    });
  }
  if (window.gsap && window.ScrollTrigger) reveals();
  else {
    var tries = 0;
    var poll = setInterval(function () {
      if ((window.gsap && window.ScrollTrigger) || tries++ > 40) { clearInterval(poll); reveals(); }
    }, 100);
  }
})();
